/**
 * Shared onboarding: ensure new registered users have one welcome cookbook with one Python card.
 * Idempotent — safe to call from Auth and App; skipped for anonymous guests. Only creates for
 * users who have no onboarding cookbook, have not dismissed it, and have no other decks yet.
 * Uses a per-uid in-flight lock so concurrent auth calls cannot duplicate decks.
 */

import {
  collection,
  doc,
  query,
  where,
  limit,
  getDoc,
  getDocs,
  setDoc,
  runTransaction,
  writeBatch,
} from 'firebase/firestore';
import { auth, db } from '../firebase/firebaseConfig';
import LevelingSystem from '../components/SRS/LevelingSystem';

/** One ensureOnboardingCookbook run per uid at a time (App + Auth fire together on load). */
const ensureInFlight = new Map();

const onboardingCookbooksQuery = (uid) =>
  query(
    collection(db, `users/${uid}/decks`),
    where('onboardingCookbook', '==', true)
  );

/**
 * True when the user explicitly removed the welcome cookbook (or we inferred they
 * no longer need it). Persisted on users/{uid} so it survives reloads and devices.
 */
async function hasDismissedOnboardingCookbook(uid) {
  if (!uid) return false;
  try {
    const userSnap = await getDoc(doc(db, 'users', uid));
    return userSnap.exists() && userSnap.data()?.onboardingCookbookDismissed === true;
  } catch (err) {
    console.warn('[onboarding] failed to read dismissal flag:', err);
    return false;
  }
}

/** Mark welcome cookbook as dismissed so ensureOnboardingCookbook will not recreate it. */
export async function markOnboardingCookbookDismissed(uid) {
  if (!uid) return;
  try {
    await setDoc(
      doc(db, 'users', uid),
      {
        onboardingCookbookDismissed: true,
        onboardingCookbookDismissedAt: new Date().toISOString(),
      },
      { merge: true }
    );
  } catch (err) {
    console.warn('[onboarding] failed to persist dismissal flag:', err);
  }
}

/** True if the user already has any deck (welcome or otherwise). */
async function userHasAnyDecks(uid) {
  const snap = await getDocs(query(collection(db, `users/${uid}/decks`), limit(1)));
  return !snap.empty;
}

const sortOnboardingDocsOldestFirst = (docs) =>
  [...docs].sort((a, b) => {
    const ta = a.data()?.createdAt || '';
    const tb = b.data()?.createdAt || '';
    return String(ta).localeCompare(String(tb));
  });

/**
 * Keep the oldest onboarding cookbook; delete duplicate welcome decks + cards.
 */
async function dedupeOnboardingCookbooks(uid, docs) {
  if (!docs || docs.length <= 1) return docs?.[0] || null;

  const sorted = sortOnboardingDocsOldestFirst(docs);
  const keep = sorted[0];
  const extras = sorted.slice(1);

  for (const deckDoc of extras) {
    try {
      const cardsSnap = await getDocs(
        collection(db, `users/${uid}/decks/${deckDoc.id}/cards`)
      );
      const batch = writeBatch(db);
      cardsSnap.docs.forEach((cardDoc) => {
        batch.delete(cardDoc.ref);
      });
      batch.delete(deckDoc.ref);
      await batch.commit();
      console.info('[onboarding] removed duplicate welcome cookbook:', deckDoc.id);
    } catch (err) {
      console.warn('[onboarding] failed to remove duplicate cookbook:', deckDoc.id, err);
    }
  }

  return keep;
}

/**
 * Return the canonical onboarding cookbook id for this user (dedupes if needed).
 * @param {string} uid
 * @returns {Promise<string|null>}
 */
export async function getOnboardingCookbookId(uid) {
  if (!uid) return null;
  const snap = await getDocs(onboardingCookbooksQuery(uid));
  if (snap.empty) return null;
  if (snap.docs.length > 1) {
    const kept = await dedupeOnboardingCookbooks(uid, snap.docs);
    return kept?.id || null;
  }
  return snap.docs[0].id;
}

/**
 * Ensure the user has exactly one onboarding cookbook. Creates one if missing.
 * Skipped for anonymous guests — they pick a track instead of getting Hello World.
 * @param {{ uid: string, isAnonymous?: boolean }} user - Firebase user object (or { uid }).
 * @returns {Promise<void>}
 */
export async function ensureOnboardingCookbook(user) {
  if (!user?.uid) return;
  if (user.isAnonymous || auth.currentUser?.isAnonymous) return;

  const uid = user.uid;
  if (ensureInFlight.has(uid)) {
    return ensureInFlight.get(uid);
  }

  const run = (async () => {
    if (auth.currentUser?.uid !== uid) return;

    let snap = await getDocs(onboardingCookbooksQuery(uid));
    if (auth.currentUser?.uid !== uid) return;

    if (snap.docs.length > 1) {
      await dedupeOnboardingCookbooks(uid, snap.docs);
      return;
    }

    if (!snap.empty) return;

    if (await hasDismissedOnboardingCookbook(uid)) return;
    if (await userHasAnyDecks(uid)) return;

    const timestamp = new Date().toISOString();
    const levelingSystem = new LevelingSystem(uid);
    const cookbookLeveling = levelingSystem.initializeCookbookLeveling();
    const cardLeveling = levelingSystem.initializeSnippetLeveling();

    const cookbookData = {
      name: 'Welcome to FlashCode',
      description: 'Your first cookbook. Study this card to learn how FlashCode works.',
      language: 'Python',
      type: 'cookbook',
      onboardingCookbook: true,
      tags: ['beginner', 'python', 'welcome'],
      createdAt: timestamp,
      updatedAt: timestamp,
      lastAccessed: timestamp,
      userId: uid,
      ...cookbookLeveling,
    };

    const cardData = {
      title: 'Hello, World!',
      description:
        'This is your first code snippet. Learn how to use FlashCode by studying this example.',
      solution: '# This is a simple Python program that prints a message\nprint("Hello, World!")',
      notes:
        'Welcome to FlashCode! This demonstrates basic output. Feel free to modify it and experiment!',
      language: 'Python',
      creationSource: 'onboarding',
      tags: ['beginner', 'python', 'welcome'],
      createdAt: timestamp,
      updatedAt: timestamp,
      currentLineIndex: 0,
      ...cardLeveling,
    };

    if (auth.currentUser?.uid !== uid) return;

    // Re-check under the lock — a parallel caller may have finished while we waited.
    snap = await getDocs(onboardingCookbooksQuery(uid));
    if (!snap.empty) {
      if (snap.docs.length > 1) {
        await dedupeOnboardingCookbooks(uid, snap.docs);
      }
      return;
    }

    const decksRef = collection(db, `users/${uid}/decks`);
    await runTransaction(db, async (transaction) => {
      const deckRef = doc(decksRef);
      const cardsRef = collection(db, `users/${uid}/decks/${deckRef.id}/cards`);
      const cardRef = doc(cardsRef);
      transaction.set(deckRef, cookbookData);
      transaction.set(cardRef, cardData);
    });
  })();

  ensureInFlight.set(uid, run);
  try {
    await run;
  } finally {
    ensureInFlight.delete(uid);
  }
}
